import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * CopilotCard — plain-English chat with the operator. The user types
 * questions like "why is bus 1000 held?" or "which corridor is worst?" and
 * gets a streaming, grounded answer. The context sent to GPT is a tight
 * state-card (top alerts + vehicle summary), not a raw dump — keeps
 * tokens sane and answers grounded.
 *
 * Wire: POST /api/copilot/chat (SSE, `data: {"delta": "..."}`). The
 * backend proxies to OpenAI so the key never touches the browser.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { MockPill } from "../common/MockPill";
import { getBaseUrl } from "../../lib/api";
const SUGGESTIONS = [
    "Why is the top alert firing?",
    "Which bus is nearest to bunching?",
    "Summarize the demo in one sentence.",
];
const card = {
    padding: "14px 18px 18px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    minHeight: 340,
};
const conv = {
    border: "1px solid var(--color-pebble)",
    borderRadius: 6,
    padding: 12,
    flex: "1 1 auto",
    minHeight: 220,
    maxHeight: 440,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: "#fafaf7",
    scrollBehavior: "smooth",
};
const bubbleBase = {
    fontSize: 13,
    lineHeight: 1.5,
    padding: "8px 12px",
    borderRadius: 10,
    maxWidth: "88%",
    whiteSpace: "pre-wrap",
};
const userBubble = {
    ...bubbleBase,
    alignSelf: "flex-end",
    background: "#000d10",
    color: "#f6e6df",
};
const asstBubble = {
    ...bubbleBase,
    alignSelf: "flex-start",
    background: "#ffffff",
    border: "1px solid var(--color-pebble)",
    color: "var(--color-deep-ink, #000d10)",
};
const formStyle = {
    display: "flex",
    gap: 8,
};
const input = {
    flex: "1 1 auto",
    padding: "10px 12px",
    fontSize: 13,
    border: "1px solid var(--color-pebble)",
    borderRadius: 4,
    outline: "none",
    fontFamily: "inherit",
};
const sendBtn = {
    padding: "10px 14px",
    background: "#000d10",
    color: "#f6e6df",
    border: "1px solid #000d10",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    cursor: "pointer",
};
const chip = {
    fontSize: 11,
    padding: "5px 10px",
    border: "1px solid var(--color-pebble)",
    borderRadius: 999,
    background: "#f6f5f2",
    cursor: "pointer",
    color: "var(--color-deep-ink, #000d10)",
};
function buildContext(alerts, envelope) {
    const activeAlerts = alerts
        .filter((a) => a.displayState === "active")
        .slice(0, 4)
        .map((a) => ({
        id: a.id,
        pair: a.vehicles,
        forecast_gap_s: Math.round(a.forecast_gap_s),
        forecast_lead_s: Math.round(a.forecast_lead_s),
        suggested_hold_bus: a.suggested_hold_bus,
        suggested_hold_stop: a.suggested_hold_stop,
        suggested_hold_s: a.suggested_hold_s,
        headline: a.headline,
    }));
    const entities = envelope?.entities ?? [];
    // Fleet index — ALL vehicles in a compact form (id + route + lat + lng +
    // bearing + hint). ~55 chars/vehicle × 200 vehicles ≈ 11KB well under
    // gpt-4o-mini's 128k window. This lets GPT answer "route for
    // DL51EV9037?" or "nearest bus to Noida?" without hallucinating.
    const fleet = entities.map((v) => ({
        id: v.vehicle.id,
        route: v.trip.route_id,
        lat: Math.round(v.position.lat * 10000) / 10000,
        lng: Math.round(v.position.lng * 10000) / 10000,
        dir: v.trip.direction_id,
        hint: v.ui_hint,
    }));
    // Route index — { route_id: [vehicle_id, ...] } lets GPT answer
    // "which route has most buses" or "routes with 3+ vehicles".
    const routeIndex = {};
    for (const v of entities) {
        (routeIndex[v.trip.route_id] ??= []).push(v.vehicle.id);
    }
    const routeCounts = Object.entries(routeIndex)
        .map(([r, ids]) => ({ route: r, n: ids.length }))
        .sort((a, b) => b.n - a.n)
        .slice(0, 20);
    return {
        corridor: envelope?.simulated
            ? "Andheri Stn → SEEPZ, Mumbai (AS-4 mock)"
            : "Delhi DTC/DIMTS · all routes citywide (live OTD)",
        controller: "Sanchez-Martinez MPC + Berrebi lambda",
        baseline: "Daganzo 2009",
        active_alerts: activeAlerts,
        active_alert_count: activeAlerts.length,
        fleet_size: entities.length,
        unique_routes: Object.keys(routeIndex).length,
        fleet, // FULL fleet index — GPT should cite from this
        top_routes_by_vehicle_count: routeCounts,
        simulated: envelope?.simulated ?? true,
    };
}
export function CopilotCard({ alerts, envelope }) {
    const [turns, setTurns] = useState([]);
    const [draft, setDraft] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [enabled, setEnabled] = useState(null);
    const [model, setModel] = useState(null);
    const [error, setError] = useState(null);
    const scrollRef = useRef(null);
    useEffect(() => {
        fetch(`${getBaseUrl()}/api/copilot/status`)
            .then((r) => r.json())
            .then((j) => {
            setEnabled(!!j.enabled);
            setModel(j.chat_model ?? null);
        })
            .catch(() => setEnabled(false));
    }, []);
    useEffect(() => {
        // D8: auto-scroll to the latest turn so streaming tokens never fall
        // off the bottom of the container. behavior: 'smooth' pairs with the
        // CSS scroll-behavior above so long GPT answers glide instead of jump.
        const el = scrollRef.current;
        if (!el)
            return;
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }, [turns]);
    const context = useMemo(() => buildContext(alerts, envelope), [alerts, envelope]);
    const send = async (message) => {
        if (!message.trim() || streaming)
            return;
        setError(null);
        const nextTurns = [
            ...turns,
            { role: "user", content: message.trim() },
            { role: "assistant", content: "" },
        ];
        setTurns(nextTurns);
        setDraft("");
        setStreaming(true);
        try {
            const resp = await fetch(`${getBaseUrl()}/api/copilot/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    messages: nextTurns.slice(0, -1).map((t) => ({
                        role: t.role,
                        content: t.content,
                    })),
                    context,
                }),
            });
            if (resp.status === 503) {
                setError("Copilot disabled — set OPENAI_API_KEY in backend/.env.");
                setEnabled(false);
                setStreaming(false);
                setTurns((prev) => prev.slice(0, -2));
                return;
            }
            if (!resp.ok || !resp.body) {
                throw new Error(`HTTP ${resp.status}`);
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { value, done } = await reader.read();
                if (done)
                    break;
                buffer += decoder.decode(value, { stream: true });
                const events = buffer.split("\n\n");
                buffer = events.pop() ?? "";
                for (const ev of events) {
                    const line = ev.split("\n").find((l) => l.startsWith("data:"));
                    if (!line)
                        continue;
                    const payload = line.slice(5).trim();
                    if (payload === "[DONE]")
                        continue;
                    try {
                        const j = JSON.parse(payload);
                        if (j.delta) {
                            setTurns((prev) => {
                                if (prev.length === 0)
                                    return prev;
                                const copy = prev.slice();
                                const last = copy[copy.length - 1];
                                if (!last)
                                    return copy;
                                copy[copy.length - 1] = {
                                    ...last,
                                    content: last.content + j.delta,
                                };
                                return copy;
                            });
                        }
                        else if (j.error) {
                            setError(j.error);
                        }
                    }
                    catch {
                        // ignore bad JSON chunk
                    }
                }
            }
        }
        catch (e) {
            setError(String(e.message ?? e));
        }
        finally {
            setStreaming(false);
        }
    };
    const submit = (e) => {
        e.preventDefault();
        void send(draft);
    };
    return (_jsxs("div", { style: card, "data-testid": "copilot-card", children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8 }, children: [enabled === null ? (_jsx(MockPill, { kind: "coming-soon", label: "LOADING" })) : enabled ? (_jsx(MockPill, { kind: "live", label: `GPT · ${model ?? "chat"}` })) : (_jsx(MockPill, { kind: "simulated", label: "COPILOT DISABLED" })), _jsx("span", { style: {
                            fontSize: 12,
                            color: "var(--color-cool-ash)",
                        }, children: "Grounded on live alert + vehicle state" })] }), _jsx("div", { style: conv, ref: scrollRef, "data-testid": "copilot-conv", children: turns.length === 0 ? (_jsx("div", { style: {
                        fontSize: 12,
                        color: "var(--color-cool-ash)",
                        padding: "8px 4px",
                    }, children: "Ask about any bus, alert, or the A/B run. Answers cite live JSON \u2014 not the model's memory." })) : (turns.map((t, i) => (_jsx("div", { style: t.role === "user" ? userBubble : asstBubble, children: t.content || (_jsx("span", { "aria-hidden": true, style: { opacity: 0.5, fontStyle: "italic" }, children: "\u258D" })) }, i)))) }), error ? (_jsx("div", { style: {
                    fontSize: 12,
                    padding: "8px 10px",
                    background: "#f6e6df",
                    border: "1px solid #e5c3b3",
                    color: "#7a4a38",
                    borderRadius: 4,
                }, children: error })) : null, _jsx("div", { style: { display: "flex", gap: 6, flexWrap: "wrap" }, children: SUGGESTIONS.map((s) => (_jsx("button", { type: "button", style: chip, disabled: streaming || !enabled, onClick: () => void send(s), children: s }, s))) }), _jsxs("form", { style: formStyle, onSubmit: submit, children: [_jsx("input", { style: input, value: draft, onChange: (e) => setDraft(e.target.value), placeholder: enabled === false
                            ? "Copilot disabled — check backend/.env"
                            : "Ask about a bus, alert, or the sim…", disabled: streaming || !enabled, "data-testid": "copilot-input" }), _jsx("button", { type: "submit", style: sendBtn, disabled: streaming || !enabled || !draft.trim(), "data-testid": "copilot-send", children: streaming ? "…" : "Send" })] })] }));
}
